<script>
	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import frameStep from './images/LP-Are_Your_Windows_Fit_For_Indows-image_02.svg';
	import frameDepth from './images/LP-Are_Your_Windows_Fit_For_Indows-image_03.svg';
	import levelApp from './images/LP-Are_Your_Windows_Fit_For_Indows-image_04.svg';
	import behindBlinds from './images/LP-Are_Your_Windows_Fit_For_Indows-image_05.jpg';
	import blinds from './images/LP-Are_Your_Windows_Fit_For_Indows-image_06.svg';
	import hardware from './images/LP-Are_Your_Windows_Fit_For_Indows-image_07.svg';
	import uneven from './images/LP-Are_Your_Windows_Fit_For_Indows-image_08.svg';
	import customShapes from './images/LP-Are_Your_Windows_Fit_For_Indows-image_09.jpg';
	import bathroomBlinds from './images/LP-Are_Your_Windows_Fit_For_Indows-image_10.jpg';
	import deckDoor from './images/LP-Are_Your_Windows_Fit_For_Indows-image_11.jpg';
</script>

<Section
	class="
   max-md:pb-0
   max-md:[&_h2]:w-full max-md:[&_h2]:text-left
   max-md:[&_p]:text-left
 "
>
	<p>
		Indow inserts are so effective at keeping out cold (or hot) air, noises, and
		other unwanted nuisances because they form a nearly airtight seal in your
		window frames. Ensuring a snug fit in your windows is the key to realizing
		the awesome performance of Indow inserts! Contact your Fit Specialist or
		<a href="/how-to/get-indow-inserts">Start An Estimate</a> if you have any questions
		about whether your windows are fit for Indows.
	</p>
	<h2>Frame Steps & Frame Depth</h2>
	<p>
		<span class="font-bold">Frame step</span> is the flat surface within your window
		frame and where your insert will be installed. Some windows may have multiple
		frame steps, while others will just have one.
	</p>
	<Image src={frameStep} class="no-bleed" loading="eager" />
	<p>
		<span class="font-bold">Frame depth</span> is how deep your frame step is.
		To ensure a secure seal, a window frame must have an unobstructed frame step
		that is at least ⅝" deep all the way around (<span class="italic"
			>see <a href="#common-obstructions">Common Obstructions</a> below</span
		>).
	</p>
	<Bubble backgroundColor="blue">
		<h2 class="uppercase">Maximize Performance</h2>
		<p>
			When deciding where you'll install your insert, it's important to choose a
			frame step at least 1/2" from the window glass to maximize performance.
		</p>
		<p>
			If you want your inserts to <span class="font-bold"
				>block outside noise</span
			>, the inserts should be installed in a frame step that's at least 1-4"
			from the window glass - the greater distance from your window - the more
			noise reduction you'll experience.
		</p>
	</Bubble>

	<Image src={frameDepth} class="no-bleed" />
	<p>
		Rarely, some windows will have sloped frame steps. For Indow inserts to fit,
		the slope cannot exceed 15 degrees from level, and the sloped frame step
		must be at least ¾" wide. Use a level or an inclinometer to determine the
		degree of the slope.
	</p>
	<Image src={levelApp} />
</Section>
<Section
	backgroundColor="blue"
	imageColumn={behindBlinds}
	columnPosition="left"
	imageColumnAfter
	class="max-md:[&_h2]:w-full max-md:[&_h2]:text-left"
>
	<h2 id="common-obstructions" class="max-w-prose">Common Obstructions</h2>
	<p class="max-w-prose text-left text-indow-grey-dark">
		The presence of obstructions along the frame step can prevent the inserts
		from forming a tight seal. Check for the following along your chosen frame
		step:
	</p>
	<ul class="max-w-prose list-none">
		<li>
			<Image src={blinds} class="max-w-40" />
			<h3 class="text-indow-grey-dark">Blinds or Curtains</h3>
			<p class="text-left text-indow-grey-dark">
				Blinds and curtains mounted to the face of the window frame (or on the
				wall) are generally compatible, while those mounted inside the frame
				might interfere with the insert. We can help you understand how to alter
				your blinds and curtains - check out our <a
					href="/guides/using-indow-with-blinds-shutters">Blinds & Shutters</a
				> guide for more information.
			</p>
		</li>
		<li>
			<Image src={hardware} class="max-w-40" />
			<h3 class="text-indow-grey-dark">Window Hardware</h3>
			<p class="text-left text-indow-grey-dark">
				Hardware like locks or levers that protrude into the selected frame step
				can cause compatibility issues with Indow inserts.
			</p>
		</li>
		<li>
			<Image src={uneven} class="max-w-40" />
			<h3 class="text-indow-grey-dark">Uneven Frames</h3>
			<p class="text-left text-indow-grey-dark">
				Frames with a thick, uneven texture or notches along the frame step may
				not be suitable for window inserts, as a tight seal cannot be achieved.
			</p>
		</li>
	</ul>
</Section>
<Section
	class="
   max-md:pb-0
   max-md:[&_h2]:w-full max-md:[&_h2]:text-left
   max-md:[&_p]:text-left
 "
>
	<h2>Uniquely Shaped Windows</h2>
	<p>
		Indow inserts come in all shapes and sizes! We offer custom cuts in a
		variety of shapes including arched windows and trapezoids. Contact us and we
		will work with you to make inserts for your oddly shaped windows.
	</p>
	<Image src={customShapes} />
</Section>
<Section
	class="
   max-md:pb-0
   max-md:[&_h2]:w-full max-md:[&_h2]:text-left
   max-md:[&_p]:text-left
 "
>
	<h2>Compatible Spaces</h2>
	<p>
		Indow inserts are only suitable for indoor use and are not suitable for
		external use or windows without existing glass.
	</p>
	<p>
		Indow inserts can even be used in skylights with additional accessories. <a
			href="/how-to/get-indow-inserts">Contact us</a
		> for more information.
	</p>
	<Image src={bathroomBlinds} />
</Section>
<Section
	class="
   max-md:pb-0
   max-md:[&_h2]:w-full max-md:[&_h2]:text-left
   max-md:[&_p]:text-left
 "
>
	<h2>Incompatible Spaces</h2>
	<p>The following spaces are not applicable for use with window inserts:</p>
	<ul
		class="
    mb-8
    sm:w-fit
  "
	>
		<li><p>Fireplaces</p></li>
		<li><p>Sliding Doors</p></li>
		<li><p>RVs</p></li>
		<li><p>Triangles</p></li>
		<li><p>Circles</p></li>
		<li><p>Octagons</p></li>
		<li><p>Pentagons</p></li>
	</ul>
	<Image src={deckDoor} />
</Section>
<GetAFreeEstimate />
